<template>
  <div class="stock-list-container">
    <!-- Toast Notifications -->
    <CToaster :autohide="3000" class="toast-container">
      <CToast
        v-for="(info, index) in infoList"
        :key="`toast-${index}-${Date.now()}`"
        :show="true"
        :header="info.header"
        :color="info.color"
      >
        {{ info.message }}.
      </CToast>
    </CToaster>

    <!-- Active Filters Section -->
    <div class="active-filters-section" v-if="hasActiveFilters">
      <div class="filters-header d-flex justify-content-between align-items-center">
        <h6 class="mb-2">Active Filters:</h6>
        <CButton size="sm" color="secondary" variant="ghost" @click="onCleanerClick">
          <i class="fas fa-times-circle me-1"></i>
          Clear All
        </CButton>
      </div>
      <div class="filters-badges">
        <CBadge
          v-if="columnFilterValue.stockNo"
          color="primary"
          class="filter-badge"
        >
          Stock No: {{ columnFilterValue.stockNo }}
          <CButton
            close
            size="sm"
            @click="columnFilterValue.stockNo = ''; refreshTable()"
            class="filter-close"
          ></CButton>
        </CBadge>

        <CBadge
          v-if="columnFilterValue.lotNo"
          color="primary"
          class="filter-badge"
        >
          Loc No: {{ columnFilterValue.lotNo }}
          <CButton
            close
            size="sm"
            @click="columnFilterValue.lotNo = ''; refreshTable()"
            class="filter-close"
          ></CButton>
        </CBadge>

        <CBadge
          v-if="columnFilterValue.chasisNo"
          color="primary"
          class="filter-badge"
        >
          Chassis No: {{ columnFilterValue.chasisNo }}
          <CButton
            close
            size="sm"
            @click="columnFilterValue.chasisNo = ''; refreshTable()"
            class="filter-close"
          ></CButton>
        </CBadge>

        <CBadge
          v-if="columnFilterValue.brandModelName"
          color="primary"
          class="filter-badge"
        >
          Brand/Model: {{ columnFilterValue.brandModelName }}
          <CButton
            close
            size="sm"
            @click="columnFilterValue.brandModelName = ''; refreshTable()"
            class="filter-close"
          ></CButton>
        </CBadge>

        <CBadge
          v-if="priceMinFilter || priceMaxFilter"
          color="primary"
          class="filter-badge"
        >
          Price: {{ priceMinFilter || "0" }} - {{ priceMaxFilter || "∞" }}
          <CButton
            close
            size="sm"
            @click="clearPriceFilter"
            class="filter-close"
          ></CButton>
        </CBadge>

        <CBadge
          v-if="yearFromFilter || yearToFilter || monthFromFilter || monthToFilter"
          color="primary"
          class="filter-badge"
        >
          Date Range: {{ formatDateRange() }}
          <CButton
            close
            size="sm"
            @click="clearDateFilter"
            class="filter-close"
          ></CButton>
        </CBadge>
      </div>
    </div>

    <!-- Professional Filter Section -->
    <CRow class="mb-4">
      <CCol>
        <CCard class="filter-card">
          <CCardHeader class="filter-header" @click="toggleFilters">
            <div class="d-flex justify-content-between align-items-center">
              <div class="header-content">
                <i class="fas fa-filter me-2 text-primary"></i>
                <strong>Filters</strong>
                <i
                  :class="filtersExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                  class="ms-2 text-muted"
                ></i>
              </div>
              <CBadge color="secondary" class="results-badge">
                {{ computedItems.length }} Results
              </CBadge>
            </div>
          </CCardHeader>
          <CCardBody class="filter-body" v-show="filtersExpanded">
            <!-- Search Filters Section -->
            <div class="filter-section">
              <h6 class="filter-section-title">
                <i class="fas fa-search me-2"></i>
                Search Filters
              </h6>
              <CRow class="g-3">
                <CCol lg="3" md="4" cols="12">
                  <CInput
                    label="Stock No"
                    v-model="columnFilterValue.stockNo"
                    placeholder="e.g. SA001"
                  />
                </CCol>
                <CCol lg="3" md="4" cols="12">
                  <CInput
                    label="Loc No"
                    v-model="columnFilterValue.lotNo"
                    placeholder="e.g. SA1"
                  />
                </CCol>
                <CCol lg="3" md="4" cols="12">
                  <CInput
                    label="Chassis No"
                    v-model="columnFilterValue.chasisNo"
                    placeholder="e.g. GGH30"
                  />
                </CCol>
                <CCol lg="3" md="4" cols="12">
                  <CInput
                    label="Brand / Model"
                    v-model="columnFilterValue.brandModelName"
                    placeholder="e.g. Toyota Alphard"
                  />
                </CCol>
              </CRow>
            </div>

            <!-- Month/Year Range Section -->
            <div class="filter-section">
              <h6 class="filter-section-title">
                <i class="fas fa-calendar-alt me-2"></i>
                Vehicle Month/Year Range
              </h6>
              <CRow class="g-3">
                <CCol md="3" cols="6">
                  <CInput
                    label="From Month"
                    v-model="monthFromFilter"
                    placeholder="e.g. 1"
                    type="number"
                    min="1"
                    max="12"
                  />
                </CCol>
                <CCol md="3" cols="6">
                  <CInput
                    label="From Year"
                    v-model="yearFromFilter"
                    placeholder="e.g. 2020"
                    type="number"
                    min="1900"
                    max="2100"
                  />
                </CCol>
                <CCol md="3" cols="6">
                  <CInput
                    label="To Month"
                    v-model="monthToFilter"
                    placeholder="e.g. 12"
                    type="number"
                    min="1"
                    max="12"
                  />
                </CCol>
                <CCol md="3" cols="6">
                  <CInput
                    label="To Year"
                    v-model="yearToFilter"
                    placeholder="e.g. 2024"
                    type="number"
                    min="1900"
                    max="2100"
                  />
                </CCol>
              </CRow>
            </div>

            <!-- Price Range Section -->
            <div class="filter-section">
              <h6 class="filter-section-title">
                <i class="fas fa-dollar-sign me-2"></i>
                Price Range (MYR)
              </h6>
              <CRow class="g-3">
                <CCol md="6" cols="12">
                  <CInput
                    label="Minimum Price"
                    v-model="priceMinFilter"
                    placeholder="e.g. 20,000"
                    type="number"
                  />
                </CCol>
                <CCol md="6" cols="12">
                  <CInput
                    label="Maximum Price"
                    v-model="priceMaxFilter"
                    placeholder="e.g. 500,000"
                    type="number"
                  />
                </CCol>
              </CRow>
            </div>

            <div class="filter-actions">
              <CButton
                color="secondary"
                variant="outline"
                @click="onCleanerClick"
                class="me-2"
              >
                <i class="fas fa-times me-2"></i>
                Clear All
              </CButton>
              <CButton
                color="primary"
                @click="refreshTable"
                :disabled="loading"
              >
                <i class="fas fa-search me-2"></i>
                {{ loading ? 'Searching...' : 'Search' }}
              </CButton>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Professional Vehicle List -->
    <CRow>
      <CCol sm="12">
        <CCard class="vehicle-list-card">
          <CCardHeader class="list-header">
            <div class="d-flex justify-content-between align-items-center flex-wrap">
              <div class="header-content">
                <i class="fas fa-car me-2 text-primary"></i>
                <strong>Vehicle Inventory</strong>
                <span 
                  class="info-tooltip ms-2"
                  v-c-tooltip.hover="{
                    content: 'Click on any row to view detailed information about the vehicle'
                  }"
                >
                  <i class="fas fa-info-circle text-muted"></i>
                </span>
              </div>
              <div class="status-summary d-flex flex-wrap gap-2">
                <CBadge color="success" class="status-count">
                  {{ computedItems.filter(item => item.stockStatus.includes('Open')).length }} Available
                </CBadge>
                <CBadge color="warning" class="status-count">
                  {{ computedItems.filter(item => item.stockStatus.includes('Booking')).length }} Booked
                </CBadge>
                <CBadge color="danger" class="status-count">
                  {{ computedItems.filter(item => item.stockStatus.includes('Sold')).length }} Sold
                </CBadge>
              </div>
            </div>
          </CCardHeader>
          <CCardBody class="list-body p-0">
            <!-- Mobile Card View (visible on small screens) -->
            <div class="mobile-card-view d-md-none">
              <CRow class="mb-3 px-3 pt-3">
                <CCol xs="12">
                  <CSelect
                    :value.sync="itemsPerPage"
                    :options="itemsPerPageOptions"
                    @update:value="onItemsPerPageChange"
                    label="Items per page"
                    size="sm"
                  />
                </CCol>
              </CRow>
              <div v-if="loading" class="loading-state">
                <div class="text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                  <p class="mt-3 text-muted">Loading vehicle data...</p>
                </div>
              </div>
              
              <div v-else-if="computedItems.length === 0" class="empty-state">
                <div class="text-center py-5">
                  <i class="fas fa-car fa-3x text-muted mb-3"></i>
                  <h5 class="text-muted">No vehicles found</h5>
                  <p class="text-muted">Try adjusting your search criteria or clear all filters.</p>
                  <CButton color="primary" variant="outline" @click="onCleanerClick">
                    <i class="fas fa-refresh me-2"></i>
                    Clear Filters
                  </CButton>
                </div>
              </div>

              <div v-else class="mobile-cards">
                <div 
                  v-for="(item, index) in computedItems" 
                  :key="item.id"
                  class="mobile-card"
                  @click="toggleDetails(item, index)"
                >
                  <div class="mobile-card-header">
                    <div class="d-flex justify-content-between align-items-start">
                      <div class="vehicle-info">
                        <h6 class="mb-1">{{ item.brandModelName }}</h6>
                        <small class="text-muted">{{ item.yearMonth }} • Stock: {{ item.stockNo }}</small>
                      </div>
                      <div class="vehicle-image-mobile">
                        <img
                          :src="getImage(item)"
                          :alt="item.brandModelName"
                          class="mobile-image"
                          @error="handleImageError"
                        />
                      </div>
                    </div>
                  </div>
                  
                  <div class="mobile-card-body">
                    <div class="row g-2">
                      <div class="col-6">
                        <small class="text-muted d-block">Price</small>
                        <strong class="text-success">{{ item.price }}</strong>
                      </div>
                      <div class="col-6">
                        <small class="text-muted d-block">Status</small>
                        <CBadge 
                          v-if="item.stockStatus.includes('Open')"
                          color="success" 
                          class="mobile-badge"
                        >
                          Available
                        </CBadge>
                        <CBadge 
                          v-else-if="item.stockStatus.includes('Booking')"
                          color="warning" 
                          class="mobile-badge"
                        >
                          Booked
                        </CBadge>
                        <CBadge 
                          v-else-if="item.stockStatus.includes('Sold')"
                          color="danger" 
                          class="mobile-badge"
                        >
                          Sold
                        </CBadge>
                        <CBadge 
                          v-else
                          color="secondary" 
                          class="mobile-badge"
                        >
                          {{ getLatesStockStatusName(item) }}
                        </CBadge>
                      </div>
                    </div>
                    
                    <div class="row g-2 mt-2">
                      <div class="col-4">
                        <small class="text-muted d-block">Lot</small>
                        <span>{{ item.lotNo }}</span>
                      </div>
                      <div class="col-4">
                        <small class="text-muted d-block">Arrival</small>
                        <CBadge
                          :color="getArrivalStatusColor(item.arrivalStatus)"
                          class="mobile-badge"
                        >
                          {{ item.arrivalStatus === 'R' ? 'Received' : 'In Transit' }}
                        </CBadge>
                      </div>
                      <div class="col-4">
                        <small class="text-muted d-block">ETA</small>
                        <span>{{ getEtaDate(item) }}</span>
                      </div>
                    </div>

                    <!-- State Badges -->
                    <div class="mt-2" v-if="item.stockState">
                      <div class="d-flex flex-wrap gap-1">
                        <CBadge
                          v-if="item.stockState.includes('NEW_STOCK')"
                          color="success"
                          class="mobile-state-badge"
                        >
                          <i class="fas fa-star me-1"></i>NEW
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('BOOKING_EXPIRED')"
                          color="danger"
                          class="mobile-state-badge"
                        >
                          <i class="fas fa-exclamation-triangle me-1"></i>EXPIRED
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('BOOKING_EXPIRY')"
                          color="warning"
                          class="mobile-state-badge"
                        >
                          <i class="fas fa-clock me-1"></i>EXPIRING
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('PRICE_CHANGE')"
                          color="info"
                          class="mobile-state-badge"
                        >
                          <i class="fas fa-tag me-1"></i>PRICE UPDATED
                        </CBadge>
                      </div>
                    </div>

                    <!-- Description -->
                    <div class="mt-2" v-if="item.vehicleDescription">
                      <small class="text-muted d-block">Description</small>
                      <small>{{ truncateText(item.vehicleDescription, 80) }}</small>
                    </div>
                  </div>

                  <div class="mobile-card-footer" @click="handleRowClick(item, index, $event)">
                    <CButton
                      color="primary"
                      size="sm"
                      @click.stop="showDetails(item)"
                    >
                      <i class="fas fa-eye me-1"></i>
                      View Details
                    </CButton>
                    <small class="text-muted ms-auto">
                      <i :class="item._toggled ? 'fas fa-chevron-up' : 'fas fa-chevron-down'" class="me-1"></i>
                      Tap to {{ item._toggled ? 'collapse' : 'expand' }}
                    </small>
                  </div>

                  <!-- Mobile Details Expansion -->
                  <CCollapse :show="Boolean(item._toggled)">
                    <div class="mobile-details">
                      <hr class="my-3">
                      <div class="mobile-details-content">
                        <!-- Documents Section -->
                        <div v-if="hasDocuments(item)" class="mb-3">
                          <h6 class="mb-2">
                            <i class="fas fa-file-pdf me-2"></i>Documents
                          </h6>
                          <div class="document-links">
                            <div v-if="item.clearance && item.clearance.k8Documents && item.clearance.k8Documents.length">
                              <small class="text-muted d-block mb-1">K8 Documents:</small>
                              <div class="mb-2">
                                <a
                                  v-for="doc in item.clearance.k8Documents" 
                                  :key="doc.id"
                                  :href="getDocumentUrl(doc)"
                                  target="_blank"
                                  class="btn btn-outline-primary btn-sm me-1 mb-1"
                                >
                                  <i class="fas fa-download me-1"></i>
                                  {{ getDocumentName(doc) }}
                                </a>
                              </div>
                            </div>
                            <div v-if="item.import && item.import.billOfLandingDocuments && item.import.billOfLandingDocuments.length">
                              <small class="text-muted d-block mb-1">BOL Documents:</small>
                              <div>
                                <a
                                  v-for="doc in item.import.billOfLandingDocuments" 
                                  :key="doc.id"
                                  :href="getDocumentUrl(doc)"
                                  target="_blank"
                                  class="btn btn-outline-primary btn-sm me-1 mb-1"
                                >
                                  <i class="fas fa-download me-1"></i>
                                  {{ getDocumentName(doc) }}
                                </a>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CCollapse>
                </div>
              </div>
            </div>

            <!-- Desktop Table View (hidden on small screens) -->
            <div class="desktop-table-view d-none d-md-block">
              <CRow class="mb-3 px-3 pt-3 align-items-center">
                <CCol sm="8">
                  <!-- Existing filter/search content can stay here if any -->
                </CCol>
                <CCol sm="4" class="text-right">
                  <div class="d-flex align-items-center justify-content-end">
                    <label class="mb-0 mr-2" style="white-space: nowrap;">Items per page:</label>
                    <CSelect
                      :value.sync="itemsPerPage"
                      :options="itemsPerPageOptions"
                      @update:value="onItemsPerPageChange"
                      style="width: 150px;"
                      size="sm"
                    />
                  </div>
                </CCol>
              </CRow>
              <div class="table-responsive">
                <CDataTable
                  :items="computedItems"
                  :fields="professionalFields"
                  :column-filter="{ external: true, lazy: true }"
                  :column-filter-value.sync="columnFilterValue"
                  :sorter="{ external: true, resetable: true }"
                  :sorter-value.sync="sorterValue"
                  :items-per-page="itemsPerPage"
                  :active-page="1"
                  hover
                  :loading="loading"
                  @row-clicked="handleRowClick"
                  class="professional-table"
                >
                  <!-- Index Column -->
                  <template #show_index="{ index }">
                    <td class="text-center">
                      <span class="text-muted">{{ index + 1 }}</span>
                    </td>
                  </template>

                  <!-- Stock State Column -->
                  <template #stockState="{ item }">
                    <td>
                      <div class="d-flex flex-column gap-1">
                        <CBadge
                          v-if="item.stockState.includes('NEW_STOCK')"
                          color="success"
                          class="state-badge"
                        >
                          <i class="fas fa-star me-1"></i>NEW
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('BOOKING_EXPIRED')"
                          color="danger"
                          class="state-badge"
                        >
                          <i class="fas fa-exclamation-triangle me-1"></i>EXPIRED
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('BOOKING_EXPIRY')"
                          color="warning"
                          class="state-badge"
                        >
                          <i class="fas fa-clock me-1"></i>EXPIRING
                        </CBadge>
                        <CBadge
                          v-if="item.stockState.includes('PRICE_CHANGE')"
                          color="info"
                          class="state-badge"
                        >
                          <i class="fas fa-tag me-1"></i>PRICE UPDATED
                        </CBadge>
                      </div>
                    </td>
                  </template>

                  <!-- Stock Number Column -->
                  <template #stockNo="{ item }">
                    <td>
                      <div>
                        <strong>{{ item.stockNo }}</strong>
                        <br>
                        <small class="text-muted">Loc: {{ item.lotNo }}</small>
                        <br>
                        <small class="text-muted">
                          Lot No: {{ (item.clearance && item.clearance.lotNo) ? item.clearance.lotNo : 'N/A' }}
                        </small>
                      </div>
                    </td>
                  </template>

                  <!-- Year/Month Column -->
                  <template #yearMonth="{ item }">
                    <td>
                      <span>{{ item.yearMonth }}</span>
                    </td>
                  </template>

                  <!-- Arrival Status Column -->
                  <template #arrivalStatus="{ item }">
                    <td>
                      <CBadge
                        :color="getArrivalStatusColor(item.arrivalStatus)"
                        class="arrival-badge"
                      >
                        <i class="fas fa-shipping-fast me-1"></i>
                        {{ item.arrivalStatus === 'R' ? 'Received' : 'In Transit' }}
                      </CBadge>
                    </td>
                  </template>

                  <!-- ETA Column -->
                  <template #eta="{ item }">
                    <td>
                      <small>{{ getEtaDate(item) }}</small>
                    </td>
                  </template>

                  <!-- Stock Status Column -->
                  <template #stockStatus="{ item }">
                    <td>
                      <div class="d-flex flex-wrap gap-1">
                        <CBadge
                          v-if="item.stockStatus.includes('Open')"
                          color="success"
                          class="status-badge"
                        >
                          <i class="fas fa-check-circle me-1"></i>Available
                        </CBadge>
                        <CBadge 
                          v-if="item.stockStatus.includes('LOU')" 
                          color="info"
                          class="status-badge"
                        >
                          <i class="fas fa-file-contract me-1"></i>LOU
                        </CBadge>
                        <CBadge
                          v-if="item.stockStatus.includes('Booking')"
                          color="warning"
                          class="status-badge"
                        >
                          <i class="fas fa-calendar-check me-1"></i>Booked
                        </CBadge>
                        <CBadge
                          v-if="item.stockStatus.includes('Sold')"
                          color="danger"
                          class="status-badge"
                        >
                          <i class="fas fa-handshake me-1"></i>Sold
                        </CBadge>
                        <CBadge
                          v-if="item.stockStatus.includes('Cancelled')"
                          color="secondary"
                          class="status-badge"
                        >
                          <i class="fas fa-times-circle me-1"></i>Cancelled
                        </CBadge>
                      </div>
                    </td>
                  </template>

                  <!-- Brand Model Column -->
                  <template #brandModelName="{ item }">
                    <td>
                      <div>
                        <strong>{{ item.brandModelName }}</strong>
                        <br>
                        <small class="text-muted">{{ item.yearMonth }}</small>
                      </div>
                    </td>
                  </template>

                  <!-- Price Column -->
                  <template #price="{ item }">
                    <td>
                      <strong class="text-success">{{ item.price }}</strong>
                    </td>
                  </template>

                  <!-- Description Column -->
                  <template #vehicleDescription="{ item }">
                    <td>
                      <span :title="item.vehicleDescription">
                        {{ truncateText(item.vehicleDescription, 50) }}
                      </span>
                    </td>
                  </template>

                  <!-- Image Column -->
                  <template #show_image="{ item }">
                    <td class="text-center">
                      <img
                        :src="getImage(item)"
                        :alt="item.brandModelName"
                        class="vehicle-image"
                        @error="handleImageError"
                      />
                    </td>
                  </template>

                  <!-- Details Expansion -->
                  <template #details="{ item }">
                    <CCollapse
                      :show="Boolean(item._toggled)"
                      :duration="collapseDuration"
                    >
                      <div class="details-wrapper">
                        <CCard class="details-card">
                          <CCardHeader>
                            <div class="d-flex justify-content-between align-items-center">
                              <h6 class="mb-0">
                                <i class="fas fa-info-circle me-2"></i>
                                Vehicle Details - {{ item.stockNo }}
                              </h6>
                              <CBadge :color="getDetailStatusColor(item)">
                                {{ getLatesStockStatusName(item) }}
                              </CBadge>
                            </div>
                          </CCardHeader>
                          <CCardBody>
                            <!-- Vehicle Description -->
                            <div class="mb-3" v-if="item.vehicleDescription">
                              <h6>
                                <i class="fas fa-file-alt me-2"></i>Description
                              </h6>
                              <p>{{ item.vehicleDescription }}</p>
                            </div>

                            <!-- Documents Section -->
                            <CRow v-if="hasDocuments(item)">
                              <CCol md="6" v-if="item.clearance && item.clearance.k8Documents && item.clearance.k8Documents.length">
                                <div class="document-section">
                                  <h6>
                                    <i class="fas fa-file-pdf me-2"></i>K8 Documents
                                  </h6>
                                  <div class="document-list">
                                    <div 
                                      v-for="doc in item.clearance.k8Documents" 
                                      :key="doc.id"
                                      class="mb-2"
                                    >
                                      <a
                                        :href="getDocumentUrl(doc)"
                                        target="_blank"
                                        class="btn btn-outline-primary btn-sm"
                                      >
                                        <i class="fas fa-download me-2"></i>
                                        {{ getDocumentName(doc) }}
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </CCol>

                              <CCol md="6" v-if="item.import && item.import.billOfLandingDocuments && item.import.billOfLandingDocuments.length">
                                <div class="document-section">
                                  <h6>
                                    <i class="fas fa-file-pdf me-2"></i>BOL Documents
                                  </h6>
                                  <div class="document-list">
                                    <div 
                                      v-for="doc in item.import.billOfLandingDocuments" 
                                      :key="doc.id"
                                      class="mb-2"
                                    >
                                      <a
                                        :href="getDocumentUrl(doc)"
                                        target="_blank"
                                        class="btn btn-outline-primary btn-sm"
                                      >
                                        <i class="fas fa-download me-2"></i>
                                        {{ getDocumentName(doc) }}
                                      </a>
                                    </div>
                                  </div>
                                </div>
                              </CCol>
                            </CRow>

                            <!-- Action Buttons -->
                            <div class="mt-3 pt-3 border-top">
                              <CButton
                                color="primary"
                                @click="showDetails(item)"
                              >
                                <i class="fas fa-eye me-2"></i>
                                View Full Details
                              </CButton>
                            </div>
                          </CCardBody>
                        </CCard>
                      </div>
                    </CCollapse>
                  </template>
                </CDataTable>
              </div>

              <!-- Loading State -->
              <div v-if="loading" class="loading-state">
                <div class="text-center py-5">
                  <div class="spinner-border text-primary" role="status">
                    <span class="sr-only">Loading...</span>
                  </div>
                  <p class="mt-3 text-muted">Loading vehicle data...</p>
                </div>
              </div>

              <!-- Empty State -->
              <div v-if="!loading && computedItems.length === 0" class="empty-state">
                <div class="text-center py-5">
                  <i class="fas fa-car fa-3x text-muted mb-3"></i>
                  <h5 class="text-muted">No vehicles found</h5>
                  <p class="text-muted">Try adjusting your search criteria or clear all filters.</p>
                  <CButton color="primary" variant="outline" @click="onCleanerClick">
                    <i class="fas fa-refresh me-2"></i>
                    Clear Filters
                  </CButton>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <div class="pagination-wrapper" v-show="pages > 1">
              <CPagination
                :pages="pages"
                :active-page.sync="activePage"
                @update:active-page="refreshTable"
              />
            </div>
          </CCardBody>
          <CCardFooter class="card-footer">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div class="footer-actions">
                <!-- Space for future action buttons if needed -->
              </div>
              <div class="footer-info">
                <small class="text-muted">
                  Showing {{ computedItems.length }} of {{ totalItems }} vehicles
                </small>
              </div>
            </div>
          </CCardFooter>
        </CCard>
      </CCol>
    </CRow>
  </div>
</template>

<script>
import StockApi from "../../lib/stockApi";
import moment from "moment";
import { formatMalaysiaDate } from "@/utils/dateUtil";

// Define apiUrl - replace with your actual API URL
const apiUrl = window.config?.API_URL || process.env.VUE_APP_API_URL || 'https://localhost:44305/api/';

export default {
  name: "StockList",
  data() {
    return {
      // Filter states
      priceMinFilter: "",
      priceMaxFilter: "",
      monthFromFilter: "",
      yearFromFilter: "",
      monthToFilter: "",
      yearToFilter: "",
      filtersExpanded: false,
      loadedItems: [],
      // Pre-init filter keys so Vue 2 reactivity tracks them from first mount.
      // Without this, the first character a user types into a panel input
      // creates a brand-new property that hasActiveFilters can't see, so the
      // active-filter chip never appears.
      columnFilterValue: {
        stockNo: "",
        lotNo: "",
        chasisNo: "",
        brandModelName: "",
      },
      tableFilterValue: "",
      sorterValue: { column: "brandModelName", asc: true },
      
      // Pagination
      activePage: 1,
      itemsPerPage: 20,
      pages: 1,
      totalItems: 0,
      itemsPerPageOptions: [
        { value: 20, label: '20 per page' },
        { value: 50, label: '50 per page' },
        { value: 100, label: '100 per page' },
        { value: 500, label: '500 per page' },
        { value: 1000, label: '1000 per page' },
        { value: 999999, label: 'Show All' }
      ],

      // Document fields
      k8DocumentList: [],
      k8DocumentFieldList: [
        {
          key: "document_link",
          label: "Document",
          sorter: false,
          filter: false,
        },
      ],
      BOLDocumentList: [],
      BOLDocumentFieldList: [
        {
          key: "document_link",
          label: "Document",
          sorter: false,
          filter: false,
        },
      ],

      // Filter options
      stockStatusOptions: [
        { value: "Open", text: "Available" },
        { value: "LOU", text: "LOU" },
        { value: "Booking", text: "Booking" },
        { value: "Sold", text: "Sold" },
        { value: "Cancelled", text: "Cancelled" },
      ],
      selectedStockStatuses: ["Open", "LOU"],

      loading: true,
      items: [],
      infoList: [],
      
      // Professional standardized fields - keeping all original columns
      professionalFields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:60px",
          sorter: false,
          filter: false,
        },
        {
          key: "stockState",
          _style: "width:140px",
          label: "Status",
          sorter: false,
          filter: false,
        },
        {
          key: "stockNo",
          label: "Stock Info",
          _style: "width:120px"
        },
        {
          key: "chasisNo",
          label: "Chassis No",
          _style: "width:140px"
        },
        {
          key: "yearMonth",
          label: "Year",
          _style: "width:80px"
        },
        {
          key: "arrivalStatus",
          label: "Arrival",
          _style: "width:120px"
        },
        {
          key: "eta",
          label: "ETA",
          _style: "width:100px"
        },
        {
          key: "stockStatus",
          _style: "width:160px",
          label: "Availability",
          filter: false,
          sorter: false,
        },
        {
          key: "brandModelName",
          label: "Vehicle",
          _style: "min-width:200px"
        },
        {
          key: "price",
          label: "Price",
          filter: false,
          _style: "width:130px"
        },
        { 
          key: "vehicleDescription", 
          label: "Description", 
          sorter: false,
          _style: "min-width:200px"
        },
        {
          key: "show_image",
          label: "Image",
          filter: false,
          sorter: false,
          _style: "width:90px"
        },
      ],
      
      details: [],
      collapseDuration: 0,
      api: new StockApi(),
      warningModal: false,
      itemToDelete: {},
    };
  },

  mounted() {
    this.refreshTable();
  },

  watch: {
    reloadParams() {
      this.refreshTable();
    },
  },

  computed: {
    hasActiveFilters() {
      return !!(
        this.columnFilterValue.stockNo ||
        this.columnFilterValue.lotNo ||
        this.columnFilterValue.chasisNo ||
        this.columnFilterValue.brandModelName ||
        this.priceMinFilter ||
        this.priceMaxFilter ||
        this.yearFromFilter ||
        this.yearToFilter ||
        this.monthFromFilter ||
        this.monthToFilter
      );
    },

    reloadParams() {
      return [
        this.sorterValue,
        this.columnFilterValue,
        this.tableFilterValue,
        this.activePage,
        this.itemsPerPage,
        this.priceMinFilter,
        this.priceMaxFilter,
        this.monthFromFilter,
        this.yearFromFilter,
        this.monthToFilter,
        this.yearToFilter,
      ];
    },

    computedItems() {
      return this.loadedItems.map((item) => {
        return {
          ...item,
          latestStatus: this.getLatesStockStatusName(item),
          brandName: this.getBrandName(item),
          modelName: this.getModelName(item),
          price: this.getPricing(item),
          brandModelName: this.getBrandModelName(item),
          vehicleDescription: this.getVehicleDescription(item),
          lotNo: this.getLotNo(item),
          // Backend ships chassis as `vehicle.chasisNo` (single-s spelling in
          // the EF model). Flatten it onto the row so the table cell + filter
          // bind to a top-level key like every other column.
          chasisNo: (item.vehicle && item.vehicle.chasisNo) || "",
          yearMonth: this.getMonthYear(item),
          arrivalStatus: this.getArrivalStatus(item),
          eta: this.getEtaDate(item),
          stockStatus: this.getStockStatus(item),
          stockState: this.getStockState(item),
        };
      });
    },
  },

  methods: {
    // Toggle filters (works on all screen sizes)
    toggleFilters() {
      this.filtersExpanded = !this.filtersExpanded;
    },

    // Utility method for text truncation
    truncateText(text, maxLength) {
      if (!text) return '';
      return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
    },

    // Professional color methods
    getArrivalStatusColor(status) {
      return status === "R" ? "success" : "info";
    },

    getDetailStatusColor(item) {
      const status = this.getLatesStockStatusName(item).toLowerCase();
      if (status.includes('open') || status.includes('available')) return 'success';
      if (status.includes('sold')) return 'danger';
      if (status.includes('booking')) return 'warning';
      if (status.includes('lou')) return 'info';
      return 'secondary';
    },

    hasDocuments(item) {
      return (
        (item.clearance && item.clearance.k8Documents && item.clearance.k8Documents.length) ||
        (item.import && item.import.billOfLandingDocuments && item.import.billOfLandingDocuments.length)
      );
    },

    // Enhanced methods with better error handling
    getLotNo(item) {
      try {
        return item.showRoom?.lotNo || "N/A";
      } catch (error) {
        return "N/A";
      }
    },

    handleRowClick(item, index, event) {
      this.toggleDetails(item, index);
    },

    handleImageError(event) {
      event.target.src = '/placeholder-car.png'; // Add a placeholder image
    },

    onCleanerClick() {
      this.priceMinFilter = "";
      this.priceMaxFilter = "";
      this.monthFromFilter = "";
      this.yearFromFilter = "";
      this.monthToFilter = "";
      this.yearToFilter = "";
      this.columnFilterValue = {};
      this.tableFilterValue = "";
      this.refreshTable();
    },

    clearPriceFilter() {
      this.priceMinFilter = "";
      this.priceMaxFilter = "";
      this.refreshTable();
    },

    clearDateFilter() {
      this.monthFromFilter = "";
      this.yearFromFilter = "";
      this.monthToFilter = "";
      this.yearToFilter = "";
      this.refreshTable();
    },

    formatDateRange() {
      const parts = [];
      if (this.yearFromFilter && this.monthFromFilter) {
        parts.push(`From: ${this.monthFromFilter}/${this.yearFromFilter}`);
      } else if (this.yearFromFilter) {
        parts.push(`From: ${this.yearFromFilter}`);
      }

      if (this.yearToFilter && this.monthToFilter) {
        parts.push(`To: ${this.monthToFilter}/${this.yearToFilter}`);
      } else if (this.yearToFilter) {
        parts.push(`To: ${this.yearToFilter}`);
      }

      return parts.length > 0 ? parts.join(" ") : "Date Range";
    },

    getDocumentName(item) {
      try {
        return item.document?.fileName || "Unknown Document";
      } catch (error) {
        return "Unknown Document";
      }
    },

    getDocumentUrl(item) {
      return `${apiUrl}documents/file/${item.documentId}`;
    },

    getStockState(item) {
      try {
        let statusArray = [];
        const now = new Date();
        const threeDaysInMs = 3 * 24 * 60 * 60 * 1000;
        const sevenDaysInMs = 7 * 24 * 60 * 60 * 1000;

        const registrationDate = new Date(item.registrationDate);

        if (now - registrationDate < threeDaysInMs) {
          statusArray.push("NEW_STOCK");
        }

        if (item.isBooked) {
          const bookingExpiryDate = new Date(item.sale.bookingExpiryDate);
          if (now - bookingExpiryDate < sevenDaysInMs && now - bookingExpiryDate <= 0) {
            statusArray.push("BOOKING_EXPIRY");
          }
          if (now - bookingExpiryDate > 0) {
            statusArray.push("BOOKING_EXPIRED");
          }
        }

        const priceChangeDate = new Date(item.pricing.lastPriceChange);
        if (now - priceChangeDate < threeDaysInMs) {
          statusArray.push("PRICE_CHANGE");
        }

        return statusArray.length > 0 ? statusArray.join(" ") : "";
      } catch (error) {
        return "";
      }
    },

    getStockStatus(item) {
      let statusArray = [];
      if (item.isOpen) statusArray.push("Open");
      if (item.isLou) statusArray.push("LOU");
      if (item.isBooked) statusArray.push("Booking");
      if (item.isSold) statusArray.push("Sold");
      if (item.isCancelled) statusArray.push("Cancelled");
      if (item.isRequestDuty) statusArray.push("Request Duty");

      return statusArray.length > 0 ? statusArray.join(" ") : "";
    },

    getArrivalStatus(item) {
      return item.arrivalStateDescription === "Received" ? "R" : "I";
    },

    getArrivalBadge(status) {
      return status === "R" ? "success" : "info";
    },

    getMonthYear(item) {
      try {
        const month = item.vehicle?.month || "";
        const year = item.vehicle?.year || "";
        if (year && month) {
          const paddedMonth = String(month).padStart(2, '0');
          return `${paddedMonth}/${year}`;
        }
        if (year) {
          return `XX/${year}`;
        }
        return "N/A";
      } catch (error) {
        return "N/A";
      }
    },

    getEtaDate(item) {
      try {
        // Try both camelCase and PascalCase
        const importData = item.import || item.Import;
        if (!importData) {
          return "N/A";
        }

        const eta = importData.estimateDateOfArrival || importData.EstimateDateOfArrival;
        if (eta) {
          return formatMalaysiaDate(eta);
        }
        return "N/A";
      } catch (error) {
        console.error('Error getting ETA:', error);
        return "N/A";
      }
    },

    getVehicleDescription(item) {
      try {
        return item.vehicle?.description || "";
      } catch (error) {
        return "";
      }
    },

    removeTrailingSlash(str) {
      return str.endsWith("/") ? str.slice(0, -1) : str;
    },

    getImage(item) {
      try {
        const baseUrl = apiUrl.endsWith('/') ? apiUrl : apiUrl + '/';

        // 1. Preferred: user-chosen default photo via Vehicle.DefaultImageUrl
        //    (format "/Documents/File/{id}"). Route through the resize endpoint for
        //    ~170x smaller payload than the full-size photo.
        const defaultUrl = item.vehicle?.defaultImageUrl;
        if (defaultUrl) {
          const match = /\/Documents\/File\/([0-9a-fA-F-]{36})/i.exec(defaultUrl);
          if (match) {
            return baseUrl + "documents/Thumbnail/" + match[1] + "?size=128";
          }
          // Unrecognised format — fall back to the raw URL
          return this.removeTrailingSlash(apiUrl) + defaultUrl;
        }

        // 2. No default chosen — use first photo from the slim DTO's defaultImageId
        if (item.vehicle?.defaultImageId) {
          return baseUrl + "documents/Thumbnail/" + item.vehicle.defaultImageId + "?size=128";
        }

        // 3. Legacy fallback: older API shape with full vehiclePhotoList
        if (item.vehicle?.vehiclePhotoList && item.vehicle.vehiclePhotoList.length > 0) {
          return baseUrl + "documents/Thumbnail/" + item.vehicle.vehiclePhotoList[0].documentId + "?size=128";
        }

        return '/img/placeholder-car.png';
      } catch (error) {
        return '/img/placeholder-car.png';
      }
    },

    getPricing(item) {
      try {
        if (!item.pricing?.recommendedSalePrice) return "N/A";
        return new Intl.NumberFormat("ms-MY", {
          style: "currency",
          currency: "MYR",
        }).format(item.pricing.recommendedSalePrice);
      } catch (error) {
        return "N/A";
      }
    },

    showDetails(item) {
      this.$router.push({
        path: `/agents/vehicle/${item.id}`,
      });
    },

    getBrandName(item) {
      try {
        return item.vehicle?.brand?.name || "N/A";
      } catch (error) {
        return "N/A";
      }
    },

    getModelName(item) {
      try {
        return item.vehicle?.model?.name || "N/A";
      } catch (error) {
        return "N/A";
      }
    },

    getBrandModelName(item) {
      try {
        const brand = item.vehicle?.brand?.name || "";
        const model = item.vehicle?.model?.name || "";
        return brand && model ? `${brand} ${model}` : "N/A";
      } catch (error) {
        return "N/A";
      }
    },

    getLatesStockStatusName(item) {
      try {
        return item.latestStockStatus?.stockStatus?.name || "N/A";
      } catch (error) {
        return "N/A";
      }
    },

    onItemsPerPageChange(newValue) {
      const self = this;

      // Safety warning for "Show All" with large datasets
      if (newValue === 999999 && self.totalItems > 1000) {
        const proceed = confirm(
          `Loading all ${self.totalItems} items may be slow and affect performance.\n\nContinue?`
        );

        if (!proceed) {
          // Reset to safe value
          self.$nextTick(() => {
            self.itemsPerPage = 1000;
          });
          return;
        }
      }

      // Reset to first page when changing items per page
      self.activePage = 1;

      // Refresh the table with new items per page
      self.refreshTable();
    },

    toast(header, message, color) {
      this.infoList.push({
        header: header,
        message: message,
        color: color,
      });
    },

    toggleDetails(item, index) {
      // Find the actual item in loadedItems by ID to ensure reactivity
      const actualItem = this.loadedItems.find(i => i.id === item.id);

      if (actualItem) {
        this.$set(actualItem, "_toggled", !actualItem._toggled);
      }

      this.collapseDuration = 300;
      this.$nextTick(() => {
        this.collapseDuration = 0;
      });
    },

    refreshTable() {
      this.loading = true;

      const params = {
        page: this.activePage,
        itemsPerPage: this.itemsPerPage,
        sorter: this.sorterValue,
        filters: this.columnFilterValue,
        search: this.tableFilterValue,
        priceMin: this.priceMinFilter,
        priceMax: this.priceMaxFilter,
        monthFrom: this.monthFromFilter,
        yearFrom: this.yearFromFilter,
        monthTo: this.monthToFilter,
        yearTo: this.yearToFilter,
        isOpen: true,
      };

      this.api
        .getList(params)
        .then((response) => {
          if (response.result) {
            // Initialize _toggled property on each item for Vue reactivity
            this.loadedItems = response.result.items.map(item => {
              this.$set(item, '_toggled', false);
              return item;
            });
            this.totalItems = response.result.totalItems;
            this.pages = Math.ceil(response.result.totalItems / this.itemsPerPage);
          } else {
            this.loadedItems = [];
            this.totalItems = 0;
            this.pages = 1;
          }
          this.loading = false;
        })
        .catch((error) => {
          console.error('Error loading data:', error);
          this.toast("Error", "Failed to load vehicle data", "danger");
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
/* Professional Container - Using CoreUI's default background */
.stock-list-container {
  padding: 1.5rem;
  min-height: 100vh;
}

/* Toast Notifications */
.toast-container {
  position: fixed;
  top: 1.5rem;
  right: 1.5rem;
  z-index: 1050;
}

/* Active Filters Section */
.active-filters-section {
  background: var(--cui-card-bg, #fff);
  border-radius: 0.5rem;
  padding: 1.25rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.filters-badges {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.filter-badge {
  font-size: 0.8125rem;
  font-weight: 500;
  padding: 0.5rem 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.filter-close {
  background: none;
  border: none;
  color: inherit;
  font-size: 0.75rem;
  padding: 0;
  margin-left: 0.25rem;
  opacity: 0.8;
}

.filter-close:hover {
  opacity: 1;
}

/* Professional Filter Card */
.filter-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.filter-header {
  border-bottom: 1px solid var(--cui-border-color, #d8dbe0);
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s ease;
}

.filter-header:hover {
  background-color: #e9ecef;
}

.header-content {
  display: flex;
  align-items: center;
}

.results-badge {
  font-size: 0.8125rem;
  font-weight: 500;
}

.filter-body {
  padding: 1.5rem;
}

/* Filter Sections */
.filter-section {
  margin-bottom: 1.5rem;
}

.filter-section:last-of-type {
  margin-bottom: 0;
}

.filter-section-title {
  color: #4f5d73;
  font-size: 0.9375rem;
  font-weight: 600;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #e9ecef;
  display: flex;
  align-items: center;
}

.filter-section-title i {
  color: #768192;
  font-size: 0.875rem;
}

.filter-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.25rem;
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Professional Vehicle List Card */
.vehicle-list-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.list-header {
  border-bottom: 1px solid var(--cui-border-color, #d8dbe0);
}

.status-summary {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.status-count {
  font-size: 0.8125rem;
  font-weight: 500;
}

.info-tooltip {
  cursor: help;
}

/* Mobile Card View */
.mobile-card-view {
  padding: 1rem;
}

.mobile-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.mobile-card {
  background: var(--cui-card-bg, #fff);
  border: 1px solid var(--cui-border-color, #d8dbe0);
  border-radius: 0.5rem;
  padding: 1rem;
  cursor: pointer;
  transition: all 0.2s ease;
}

.mobile-card:hover {
  box-shadow: 0 0.25rem 0.5rem rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.mobile-card-header {
  margin-bottom: 0.75rem;
}

.vehicle-image-mobile {
  flex-shrink: 0;
}

.mobile-image {
  width: 60px;
  height: 45px;
  object-fit: cover;
  border-radius: 0.375rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.mobile-card-body {
  margin-bottom: 0.75rem;
}

.mobile-badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

.mobile-state-badge {
  font-size: 0.6875rem;
  padding: 0.25rem 0.5rem;
}

.mobile-card-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 0.75rem;
  border-top: 1px solid var(--cui-border-color-translucent, rgba(0, 0, 0, 0.125));
}

.mobile-details {
  background: var(--cui-body-bg, #f8f9fa);
  margin: 0 -1rem -1rem -1rem;
  padding: 1rem;
  border-radius: 0 0 0.5rem 0.5rem;
}

.mobile-details-content {
  background: var(--cui-card-bg, #fff);
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.document-links {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

/* Desktop Table View */
.desktop-table-view {
  /* Uses CoreUI's default table styling */
}

.professional-table {
  font-size: 0.875rem;
}

.professional-table th {
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
}

.professional-table tbody tr:hover {
  cursor: pointer;
}

/* Professional Badge Styling */
.state-badge,
.status-badge,
.arrival-badge {
  font-size: 0.6875rem;
  font-weight: 600;
  padding: 0.25rem 0.5rem;
  display: flex;
  align-items: center;
  white-space: nowrap;
}

.vehicle-image {
  width: 70px;
  height: 52px;
  object-fit: cover;
  border-radius: 0.375rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Details Section */
.details-wrapper {
  padding: 1rem;
  background: var(--cui-body-bg, #f8f9fa);
}

.details-card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

.document-section {
  background: var(--cui-body-bg, #f8f9fa);
  padding: 1rem;
  border-radius: 0.375rem;
  margin-bottom: 1rem;
  border: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Loading and Empty States */
.loading-state,
.empty-state {
  padding: 3.75rem 1.25rem;
}

/* Mobile Responsiveness */
@media (max-width: 767.98px) {
  .stock-list-container {
    padding: 1rem;
  }

  .filter-body {
    padding: 1rem;
  }

  .filter-section {
    margin-bottom: 1.25rem;
  }

  .filter-section-title {
    font-size: 0.875rem;
    margin-bottom: 0.75rem;
  }

  .filter-actions {
    flex-direction: column;
    margin-top: 1.25rem;
    padding-top: 1rem;
  }

  .filter-actions .btn {
    width: 100%;
  }
  
  .status-summary {
    flex-direction: column;
    gap: 0.375rem;
  }
  
  .filters-badges {
    flex-direction: column;
  }
  
  .filter-badge {
    align-self: flex-start;
  }
  
  .list-header .d-flex {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.75rem;
  }
}

/* Pagination */
.pagination-wrapper {
  padding: 1rem;
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
}

/* Enhanced Footer */
.card-footer {
  border-top: 1px solid var(--cui-border-color, #d8dbe0);
  background: var(--cui-body-bg, #f8f9fa);
}

.footer-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.footer-info {
  display: flex;
  align-items: center;
}

@media (max-width: 575.98px) {
  .stock-list-container {
    padding: 0.75rem;
  }
  
  .filter-body {
    padding: 1rem;
  }
  
  .list-header {
    padding: 1rem;
  }
  
  .mobile-card {
    padding: 0.75rem;
  }
  
  .mobile-image {
    width: 50px;
    height: 38px;
  }
}

/* Print Styles */
@media print {
  .stock-list-container {
    padding: 0;
  }
  
  .filter-card,
  .active-filters-section {
    display: none;
  }
  
  .mobile-card-view {
    display: none;
  }
  
  .desktop-table-view {
    display: block !important;
  }
  
  .professional-table {
    font-size: 0.75rem;
  }
  
  .vehicle-image {
    display: none;
  }
}
</style>